
import React, { useState, useRef, useEffect } from "react";
import { supabase } from "./lib/supabaseClient";

export default function Editor() {
  const [selectedVoice, setSelectedVoice] = useState("weathered");
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [isVoiceLoading, setIsVoiceLoading] = useState(false);

  // Data loading state
  const [bookId, setBookId] = useState(null);
  const [book, setBook] = useState(null);
  const [chapters, setChapters] = useState([]);
  const [characters, setCharacters] = useState([]);
  const [selectedChapterIndex, setSelectedChapterIndex] = useState(0);
  const [isDataLoading, setIsDataLoading] = useState(true);
  const [dataError, setDataError] = useState(null);
  const mainContainerRef = useRef(null);
  const chatPanelRef = useRef(null);
  const [sidePanelMinHeight, setSidePanelMinHeight] = useState(480);
  const [chatFixedLeft, setChatFixedLeft] = useState(24);
  const [chatFixedWidth, setChatFixedWidth] = useState(260);

  
  const audioRef = useRef(null);
  
  useEffect(() => {
    // Add noindex meta tag for SEO
    const metaTag = document.createElement('meta');
    metaTag.name = 'robots';
    metaTag.content = 'noindex, nofollow';
    document.head.appendChild(metaTag);

    // Parse bookId from URL: /editor/:id
    try {
      const match = window.location.pathname.match(/\/editor\/([^/]+)/);
      const id = match && match[1] ? match[1] : null;
      setBookId(id);
    } catch (_) {
      setBookId(null);
    }

    return () => {
      // Clean up meta tag when component unmounts
      const existingTag = document.head.querySelector('meta[name="robots"]');
      if (existingTag) {
        document.head.removeChild(existingTag);
      }
    };
  }, []);

  // Fetch book + chapters + characters
  useEffect(() => {
    async function loadData() {
      if (!bookId) {
        setIsDataLoading(false);
        return;
      }
      setIsDataLoading(true);
      setDataError(null);
      try {
        const [{ data: bookData, error: bookErr }, { data: chapterData, error: chErr }] = await Promise.all([
          supabase.from('books').select('*').eq('id', bookId).single(),
          supabase.from('chapters').select('*').eq('book_id', bookId).order('order', { ascending: true })
        ]);
        if (bookErr) throw bookErr;
        if (chErr) throw chErr;
        setBook(bookData || null);
        setChapters(chapterData || []);

        // Try to fetch characters via metadata → characters
        let fetchedCharacters = [];
        try {
          const { data: meta, error: metaErr } = await supabase.from('book_metadata').select('id').eq('book_id', bookId).limit(1).single();
          if (!metaErr && meta?.id) {
            const { data: charData } = await supabase.from('characters').select('*').eq('book_metadata_id', meta.id);
            fetchedCharacters = charData || [];
          }
        } catch (_) {
          // ignore quietly in early dev
        }
        setCharacters(fetchedCharacters);
        setSelectedChapterIndex(0);
      } catch (err) {
        console.error('Failed to load editor data:', err);
        setDataError(err?.message || 'Failed to load data');
      } finally {
        setIsDataLoading(false);
      }
    }
    loadData();
  }, [bookId]);

  // Position chat input to align with main container left edge
  useEffect(() => {
    const updateHeights = () => {
      try {
        const container = mainContainerRef.current;
        if (!container) return;
        const rect = container.getBoundingClientRect();
        const audioBarApproxHeight = 96; // sticky player + gap
        const available = Math.max(320, window.innerHeight - rect.top - audioBarApproxHeight);
        setSidePanelMinHeight(available);
      } catch (_) {}
    };
    updateHeights();
    window.addEventListener('resize', updateHeights);
    return () => {
      window.removeEventListener('resize', updateHeights);
    };
  }, []);

  // Position fixed chat input to align with Chat pane boundaries
  useEffect(() => {
    const updateChatFixed = () => {
      try {
        const rect = chatPanelRef.current?.getBoundingClientRect();
        if (!rect) return;
        const padding = 0; // align with card inner padding
        setChatFixedLeft(Math.round(rect.left + padding));
        // Make the chat input 26px narrower (reduce on right side)
        setChatFixedWidth(Math.max(200, Math.round(rect.width - padding * 2 - 26)));
      } catch (_) {}
    };
    updateChatFixed();
    // Update on resize/scroll to keep alignment
    window.addEventListener('resize', updateChatFixed);
    window.addEventListener('scroll', updateChatFixed, { passive: true });
    // Also run after a short delay to catch first layout
    const t = setTimeout(updateChatFixed, 0);
    return () => {
      clearTimeout(t);
      window.removeEventListener('resize', updateChatFixed);
      window.removeEventListener('scroll', updateChatFixed);
    };
  }, []);
  
  // Voice options mapping to audio files
  const voiceOptions = {
    weathered: {
      label: "American, Weathered, Male",
      file: "/audio/WEATHERED_Sun_Also_Rises,_The_-_Ernest_Hemingway.epub_CHAPTER_2.mp3"
    },
    thirties: {
      label: "American, 30's, Male", 
      file: "/audio/30s_Sun_Also_Rises,_The_-_Ernest_Hemingway.epub_CHAPTER_2.mp3"
    },
    distinguished: {
      label: "English, Distinguished, Male",
      file: "/audio/English_Distinguished_Sun_Also_Rises,_The_-_Ernest_Hemingway.epub_CHAPTER_2.mp3"
    }
  };

  // Chapter content placeholder when DB is empty
  const chapterContent = `CHAPTER 2

That winter Robert Cohn went over to America with his novel, and it was accepted by a fairly good publisher.

His going made an awful row I heard, and I think that was where Frances lost him, because several women were nice to him in New York, and when he came back he was quite changed. He was more enthusiastic about America than ever, and he was not so simple, and he was not so nice. The publishers had praised his novel pretty highly and it rather went to his head. Then several women had put themselves out to be nice to him, and his horizons had all shifted.

For four years his horizon had been absolutely limited to his wife. For three years, or almost three years, he had never seen beyond Frances. I am sure he had never been in love in his life.

He had married on the rebound from the rotten time he had in Princeton, and had not been in love since. For a man who was as shy as he was, he was very sure of himself with women. I do not know what this means.

That was one thing I had never been able to understand. I never got very clear about him, but I thought he had a nice quality, some sort of nice quality. He never came back that winter, but we heard that the novel had finally appeared in America and had gotten some very good reviews. I heard it was going to be published in England, too. I saw Frances at a café the other day. She told me about the novel. 

"I've always thought that Cohn was a bit of a simpleton," she said. "And I still think so. But he was sweet to me while it lasted."

The way she said it made me feel rather sad. I had always rather liked Cohn, even though he was one of the most boring men I had ever known. He was very romantic about everything. He had a wonderful way of making everything seem better than it really was. When he left Paris, he really believed that Frances was heartbroken. I think Frances herself believed it for a while.

But I think what really happened was that Robert Cohn never came back because he had found something in America that he had never found in Paris. And I think that something was his own life.`;

  // Initial loading timer - 4 seconds
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsInitialLoading(false);
    }, 4000);

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const updateTime = () => setCurrentTime(audio.currentTime);
    const updateDuration = () => setDuration(audio.duration);
    const handleEnded = () => {
      setIsPlaying(false);
    };

    audio.addEventListener('timeupdate', updateTime);
    audio.addEventListener('loadedmetadata', updateDuration);
    audio.addEventListener('ended', handleEnded);

    return () => {
      audio.removeEventListener('timeupdate', updateTime);
      audio.removeEventListener('loadedmetadata', updateDuration);
      audio.removeEventListener('ended', handleEnded);
    };
  }, []);

  const handlePlayPause = () => {
    const audio = audioRef.current;
    if (!audio) return;

    if (isPlaying) {
      audio.pause();
      setIsPlaying(false);
    } else {
      audio.play();
      setIsPlaying(true);
    }
  };

  const handleProgressClick = (e) => {
    const audio = audioRef.current;
    if (!audio) return;

    const rect = e.currentTarget.getBoundingClientRect();
    const percent = (e.clientX - rect.left) / rect.width;
    audio.currentTime = percent * duration;
  };

  const formatTime = (time) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const handleVoiceSelect = (voiceKey) => {
    setSelectedVoice(voiceKey);
    setIsDropdownOpen(false);
    
    // Stop current audio if playing
    if (isPlaying) {
      audioRef.current?.pause();
      setIsPlaying(false);
    }

    // Start voice loading for 2 seconds
    setIsVoiceLoading(true);
    setTimeout(() => {
      setIsVoiceLoading(false);
    }, 2000);
  };

  const renderEditableContent = () => {
    const currentChapter = chapters && chapters.length > 0 ? chapters[selectedChapterIndex] : null;
    const textToDisplay = currentChapter?.text || chapterContent;
    const titleToDisplay = currentChapter?.title || 'CHAPTER 2';
    return (
      <div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 12, color: '#666', fontWeight: 600 }}>Chapter</span>
            <select
              value={String(selectedChapterIndex)}
              onChange={(e) => setSelectedChapterIndex(Number(e.target.value))}
              style={{ border: '1px solid #e0e0e0', borderRadius: 8, padding: '6px 10px', fontSize: 14 }}
            >
              {(chapters && chapters.length > 0 ? chapters : [{ title: 'Chapter 2 (Demo)' }]).map((ch, idx) => (
                <option key={idx} value={idx}>{ch.title || `Chapter ${idx + 1}`}</option>
              ))}
            </select>
          </div>
        </div>
        <div style={{ fontWeight: 600, marginBottom: 12, color: '#333' }}>{titleToDisplay}</div>
        <div
          contentEditable
          suppressContentEditableWarning={true}
          style={{
            whiteSpace: 'pre-wrap',
            lineHeight: '1.7',
            fontSize: '15px',
            color: '#2c2c2c',
            outline: 'none',
            minHeight: '500px',
            fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif"
          }}
        >
          {textToDisplay}
        </div>
      </div>
    );
  };

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', background: '#fff' }}>
      {/* Header - matching Dashboard style */}
      <div style={{ 
        width: '100%',
        padding: '2rem 0 1.5rem 0',
        borderBottom: '1px solid #eee'
      }}>
        <div className="editor-header-content" style={{
          maxWidth: '1200px',
          margin: '0 auto',
          paddingLeft: '1.5rem',
          paddingRight: '1.5rem',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div className="logo" style={{ cursor: 'pointer' }} onClick={() => window.location.href = '/dashboard'}>
            <img alt="Light Reads logo" className="logo-img" src="/logo-quote.png" />
            <span className="logo-text">Light Reads</span>
          </div>
          <h1 className="editor-header-title" style={{ 
            fontSize: '1.5rem',
            fontWeight: '600',
            margin: 0,
            color: '#333',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            maxWidth: '50vw'
          }} title={book?.title || 'Untitled Book'}>
            {book?.title || 'Untitled Book'}
          </h1>
          <div className="editor-header-buttons" style={{ 
            display: 'flex',
            gap: '1rem'
          }}>
            <button 
              onClick={() => window.location.href = '/dashboard'}
              style={{
                background: '#fff',
                color: '#444',
                border: '1px solid #666',
                padding: '8px 16px',
                borderRadius: '6px',
                fontSize: '14px',
                fontWeight: '500',
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
              onMouseOver={(e) => {
                e.target.style.background = '#f5f5f5';
                e.target.style.borderColor = '#444';
              }}
              onMouseOut={(e) => {
                e.target.style.background = '#fff';
                e.target.style.borderColor = '#666';
              }}
            >
              Save & Exit
            </button>
            <button 
              style={{
                background: '#fff',
                color: '#444',
                border: '1px solid #666',
                padding: '8px 16px',
                borderRadius: '6px',
                fontSize: '14px',
                fontWeight: '500',
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
              onMouseOver={(e) => {
                e.target.style.background = '#f5f5f5';
                e.target.style.borderColor = '#444';
              }}
              onMouseOut={(e) => {
                e.target.style.background = '#fff';
                e.target.style.borderColor = '#666';
              }}
            >
              Export
            </button>
          </div>
        </div>
      </div>

      {/* Main Editor Content */}
      <div ref={mainContainerRef} className="editor-main-content" style={{ 
        flex: 1,
        display: 'flex',
        maxWidth: '1200px',
        margin: '0 auto',
        width: '100%',
        padding: '2rem 1.5rem 168px 1.5rem' // Reserve extra space for sticky audio player + chat input
      }}>
        {/* Left Panel - Chat */}
        <div ref={chatPanelRef} className="editor-chat-panel" style={{
          width: '260px',
          padding: '1.25rem',
          border: '1px solid #e0e0e0',
          borderRadius: '12px',
          background: '#fff',
          minHeight: sidePanelMinHeight,
          boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
          marginRight: '1.25rem',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-start',
          // no absolute input inside; we render a fixed input below
        }}>
          <h3 style={{ margin: '0 0 1rem 0', fontSize: '1.05rem', fontWeight: 500, color: '#333' }}>Chat</h3>
          <div style={{ fontSize: 13, color: '#555', lineHeight: 1.5, marginBottom: 12 }}>
            {dataError ? (
              <span style={{ color: '#d32f2f' }}>Failed to load book data. You can still try the demo editor.</span>
            ) : (
              <span>
                {isDataLoading
                  ? 'Loading your analysis and setup...'
                  : 'I\'ve scanned your book and prepared a starting setup. Make changes here or ask me to adjust text, voices, and sounds.'}
              </span>
            )}
          </div>
          {/* empty; fixed input rendered at bottom of screen */}
        </div>

        {/* Center Panel - Text Editor */}
        <div className="editor-left-panel" style={{
          flex: '1',
          marginRight: '1.25rem',
          padding: '2rem',
          border: '1px solid #e0e0e0',
          borderRadius: '12px',
          background: '#fff',
          boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', margin: '0 0 1.5rem 0' }}>
            <h3 style={{ 
              margin: 0,
              fontSize: '1.1rem',
              fontWeight: '500',
              color: '#333'
            }}>
              Edit Script
            </h3>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
              <span role="button" style={{ fontSize: 13, color: '#333', cursor: 'pointer', textDecoration: 'underline' }}>Undo</span>
              <span role="button" style={{ fontSize: 13, color: '#333', cursor: 'pointer', textDecoration: 'underline' }}>Redo</span>
              <span role="button" style={{ fontSize: 13, color: '#333', cursor: 'pointer', textDecoration: 'underline' }}>Pronunciation</span>
            </div>
          </div>
          <div style={{
            background: '#fff',
            border: '1px solid #e8e8e8',
            borderRadius: '8px',
            padding: '1.5rem',
            minHeight: '500px',
            fontSize: '15px',
            fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif"
          }}>
            {renderEditableContent()}
          </div>
        </div>

        {/* Right Panel - Voice Selection */}
        <div className="editor-right-panel" style={{
          width: '260px',
          padding: '2rem',
          border: '1px solid #e0e0e0',
          borderRadius: '12px',
          background: '#fff',
          minHeight: sidePanelMinHeight,
          boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
        }}>
          <h3 style={{ 
            margin: '0 0 1.5rem 0',
            fontSize: '1.1rem',
            fontWeight: '500',
            color: '#333'
          }}>
            Voices and Sounds
          </h3>
          
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ 
              display: 'block',
              marginBottom: '0.5rem',
              fontSize: '14px',
              fontWeight: '500',
              color: '#555'
            }}>
              Narrator
            </label>
            
            <div style={{ position: 'relative' }}>
              <button
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  border: '1px solid #e0e0e0',
                  borderRadius: '8px',
                  background: '#fff',
                  fontSize: '14px',
                  textAlign: 'left',
                  cursor: 'pointer',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  fontWeight: '500',
                  color: '#333',
                  transition: 'all 0.2s ease'
                }}
                onMouseOver={(e) => e.target.style.borderColor = '#ccc'}
                onMouseOut={(e) => e.target.style.borderColor = '#e0e0e0'}
              >
                {voiceOptions[selectedVoice].label}
                <span style={{ transform: isDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}>
                  ▼
                </span>
              </button>
              
              {isDropdownOpen && (
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  background: '#fff',
                  border: '1px solid #e0e0e0',
                  borderRadius: '8px',
                  boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                  zIndex: 1000,
                  marginTop: '4px'
                }}>
                  <div style={{ padding: '8px 0' }}>
                    <div style={{
                      padding: '8px 16px',
                      fontSize: '12px',
                      fontWeight: '600',
                      color: '#888',
                      borderBottom: '1px solid #eee'
                    }}>
                      Recent
                    </div>
                    {Object.entries(voiceOptions).map(([key, option]) => (
                      <button
                        key={key}
                        onClick={() => handleVoiceSelect(key)}
                        style={{
                          width: '100%',
                          padding: '12px 16px',
                          border: 'none',
                          background: selectedVoice === key ? '#f0f0f0' : 'transparent',
                          fontSize: '14px',
                          textAlign: 'left',
                          cursor: 'pointer',
                          borderBottom: '1px solid #f0f0f0'
                        }}
                        onMouseOver={(e) => e.target.style.background = '#f5f5f5'}
                        onMouseOut={(e) => e.target.style.background = selectedVoice === key ? '#f0f0f0' : 'transparent'}
                      >
                        {option.label}
                      </button>
                    ))}
                    <div style={{
                      padding: '12px 16px',
                      borderTop: '1px solid #eee',
                      marginTop: '8px'
                    }}>
                      <button style={{
                        background: 'none',
                        border: 'none',
                        fontSize: '14px',
                        fontWeight: '500',
                        textDecoration: 'underline',
                        cursor: 'pointer',
                        color: '#333'
                      }}>
                        Search Voices &gt;
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Characters list (read-only for now) */}
          <div>
            <div style={{ fontSize: 12, color: '#777', fontWeight: 600, marginBottom: 6 }}>Characters</div>
            {(characters && characters.length > 0 ? characters : [{ name: 'Narrator' }]).map((ch, idx) => (
              <div key={idx} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #f2f2f2' }}>
                <div style={{ fontSize: 14, color: '#333' }}>{ch.name}</div>
                <div style={{ fontSize: 12, color: '#888' }}>{idx === 0 ? voiceOptions[selectedVoice].label : 'Auto'}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Sticky Audio Player */}
      <div style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        background: '#fff',
        borderTop: '1px solid #e8e8e8',
        boxShadow: '0 -4px 16px rgba(0,0,0,0.08)',
        zIndex: 1000,
        padding: '20px 16px'
      }}>
        <div className="audio-player-content" style={{
          maxWidth: '1200px',
          margin: '0 auto',
          display: 'flex',
          alignItems: 'center',
          gap: '16px'
        }}>
          {/* Play Button */}
          <button
            className="play-button"
            onClick={!isInitialLoading && !isVoiceLoading ? handlePlayPause : undefined}
            style={{
              width: '48px',
              height: '48px',
              borderRadius: '50%',
              border: 'none',
              background: '#2c2c2c',
              color: '#fff',
              fontSize: '16px',
              cursor: (isInitialLoading || isVoiceLoading) ? 'default' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.2s ease',
              boxShadow: '0 2px 8px rgba(44,44,44,0.2)'
            }}
            onMouseOver={(e) => {
              if (!isInitialLoading && !isVoiceLoading) {
                e.target.style.background = '#404040';
              }
            }}
            onMouseOut={(e) => {
              if (!isInitialLoading && !isVoiceLoading) {
                e.target.style.background = '#2c2c2c';
              }
            }}
          >
            {(isInitialLoading || isVoiceLoading) ? (
              <div className="spinner" style={{
                width: '20px',
                height: '20px',
                border: '2px solid rgba(255,255,255,0.3)',
                borderTop: '2px solid #fff',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite'
              }} />
            ) : (
              isPlaying ? '⏸' : '▶'
            )}
          </button>

          {/* Progress Bar */}
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '12px' }}>
            <span className="audio-time" style={{ fontSize: '13px', color: '#666', minWidth: '40px', fontWeight: '500' }}>
              {formatTime(currentTime)}
            </span>
            
            <div
              onClick={handleProgressClick}
              style={{
                flex: 1,
                height: '6px',
                background: '#e8e8e8',
                borderRadius: '3px',
                cursor: 'pointer',
                position: 'relative'
              }}
            >
              <div
                style={{
                  width: `${duration ? (currentTime / duration) * 100 : 0}%`,
                  height: '100%',
                  background: '#2c2c2c',
                  borderRadius: '3px',
                  transition: 'width 0.1s ease'
                }}
              />
            </div>
            
            <span className="audio-time" style={{ fontSize: '13px', color: '#666', minWidth: '40px', fontWeight: '500' }}>
              {formatTime(duration - currentTime)}
            </span>
          </div>

          {/* Audio Element */}
          <audio
            ref={audioRef}
            src={voiceOptions[selectedVoice].file}
            preload="metadata"
          />
        </div>
      </div>

      {/* Fixed Chat Input aligned to Chat pane and above audio bar */}
      <div
        className="chat-fixed-input"
        style={{
          position: 'fixed',
          left: chatFixedLeft,
          bottom: 88, // sits just above audio bar
          width: chatFixedWidth,
          background: '#fff',
          border: '1px solid #e0e0e0',
          borderRadius: 12,
          padding: 12,
          boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
          zIndex: 1101
        }}
      >
        <div style={{ display: 'flex', gap: 8 }}>
          <input placeholder="Edit text, voices, sounds..." style={{ flex: 1, border: '1px solid #e0e0e0', borderRadius: 8, padding: '10px 12px', fontSize: 14 }} />
          <button style={{ border: '1px solid #e0e0e0', background: '#fff', borderRadius: 8, padding: '10px 12px', cursor: 'pointer' }}>Send</button>
        </div>
      </div>

      {/* Close dropdown when clicking outside */}
      {isDropdownOpen && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 999
          }}
          onClick={() => setIsDropdownOpen(false)}
        />
      )}

      {/* Responsive Styles */}
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        @media (max-width: 768px) {
          .editor-main-content {
            flex-direction: column !important;
            padding: 1rem 1rem 120px 1rem !important;
          }
          
          .editor-left-panel {
            margin-right: 0 !important;
            margin-bottom: 1.5rem !important;
            padding: 1rem !important;
          }
          
          .editor-right-panel {
            width: 100% !important;
            padding: 1rem !important;
            margin-right: 0 !important;
          }

          .chat-fixed-input { display: none !important; }
          .chat-inline-input { display: block !important; }
          
          .editor-header-content {
            flex-direction: column !important;
            align-items: flex-start !important;
            gap: 1rem !important;
          }
          
          .editor-header-title {
            font-size: 1.2rem !important;
            text-align: left !important;
          }
          
          .editor-header-buttons {
            align-self: flex-end !important;
          }
          
          .audio-player-content {
            gap: 8px !important;
          }
          
          .audio-time {
            min-width: 32px !important;
            font-size: 11px !important;
          }
        }
        
        @media (max-width: 480px) {
          .editor-header-content {
            padding-left: 1rem !important;
            padding-right: 1rem !important;
          }
          
          .editor-left-panel,
          .editor-right-panel {
            padding: 0.75rem !important;
          }
          
          .editor-header-title {
            font-size: 1rem !important;
          }
          
          .play-button {
            width: 40px !important;
            height: 40px !important;
            font-size: 14px !important;
          }
        }
      `}</style>
    </div>
  );
}